/**
 * `interviewer id → display name` for the currently selected roster.
 *
 * Resolves each id against the same two sources the Host field and the
 * edit-confirmation dialog use: `participant_default_options` (the chip
 * hydration source on edit / pool flows) first, then the module-level
 * `interviewerOptionCache` (populated as the user searches the
 * async-select). Ids that resolve to neither are simply absent — the
 * self-schedule day tooltip degrades to unnamed wording rather than
 * showing a raw id (ENG-3642).
 */
import { useMemo } from 'react';
import { getCachedInterviewerOption } from '../../../state/interviewerOptionCache';

export interface UseInterviewerNamesArgs {
  /** Selected interviewer ids, any numeric/string mix. */
  interviewerIds: Array<string | number>;
  /** Form-seeded chip options — `payloadRef.current.details`. */
  participantDefaultOptions?: unknown;
}

interface DefaultOption {
  value?: string | number;
  label?: string;
}

function toOptions(raw: unknown): DefaultOption[] {
  return (Array.isArray(raw) ? raw : []) as DefaultOption[];
}

export function useInterviewerNames({
  interviewerIds,
  participantDefaultOptions,
}: UseInterviewerNamesArgs): Record<string, string> {
  // Content keys, not identities: the caller re-maps the id array and
  // reads the options off a ref every render, so depending on the
  // objects themselves would re-mint the map — and with it the day
  // header renderer FullCalendar keys on — on every keystroke.
  const idKey = interviewerIds.map(String).join(',');
  const optionsKey = toOptions(participantDefaultOptions)
    .map((o) => `${String(o.value ?? '')}:${o.label ?? ''}`)
    .join('|');

  return useMemo(() => {
    const byId = new Map(
      toOptions(participantDefaultOptions).map(
        (d) => [String(d.value ?? ''), d.label ?? ''] as const
      )
    );
    const out: Record<string, string> = {};
    for (const id of idKey ? idKey.split(',') : []) {
      const name = byId.get(id) || getCachedInterviewerOption(id)?.label;
      if (name) out[id] = name;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey, optionsKey]);
}
